import type {
  IOverlayEventStatDB,
  StreamEventKind,
} from "../../../interfaces/OverlayStats";
import { openDatabase } from "../../../Utils/db";

export type EventStatsScope = "session" | "today" | "allTime";

export interface RecordEventInput {
  accountId: string;
  kind: StreamEventKind;
  /** Lowercased platform slug ("twitch" | "youtube" | "kick" | "restream" | "livepeer" | "unknown"). */
  platform: string;
  amountUsd?: number;
  amount?: number;
  username?: string;
  raidViewers?: number;
  /** epoch ms del evento; default Date.now() */
  timestamp?: number;
}

const dayBucketOf = (timestamp: number): string => {
  const d = new Date(timestamp);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const normalizePlatform = (p: string | undefined): string =>
  (p ?? "unknown").toLowerCase();

export class OverlayEventStatsRepository {
  async record(event: RecordEventInput): Promise<void> {
    const ts = event.timestamp ?? Date.now();
    const dayBucket = dayBucketOf(ts);
    const platform = normalizePlatform(event.platform);
    const db = await openDatabase();
    const existing = await db.overlayEventStats.get([
      event.accountId,
      event.kind,
      platform,
      dayBucket,
    ]);
    const next: IOverlayEventStatDB = existing
      ? { ...existing, count: existing.count + 1, updatedAt: ts }
      : {
          accountId: event.accountId,
          kind: event.kind,
          platform,
          dayBucket,
          count: 1,
          updatedAt: ts,
        };

    if (event.amountUsd !== undefined) {
      next.sumAmountUsd = (existing?.sumAmountUsd ?? 0) + event.amountUsd;
    }
    if (
      event.amount !== undefined &&
      (existing?.topAmount ?? -Infinity) < event.amount
    ) {
      next.topAmount = event.amount;
      next.topUser = event.username;
    }
    if (event.raidViewers !== undefined) {
      next.raidViewersSum = (existing?.raidViewersSum ?? 0) + event.raidViewers;
    }

    await db.overlayEventStats.put(next);
  }

  /**
   * @param platforms Optional whitelist of lowercased platform slugs. When
   *                  omitted the result aggregates across every platform.
   */
  async getScope(
    accountId: string,
    kind: StreamEventKind,
    scope: EventStatsScope,
    sessionStartedAt?: number,
    platforms?: string[],
  ): Promise<{
    count: number;
    sumAmountUsd: number;
    topAmount: number;
    topUser?: string;
    raidViewersSum: number;
  }> {
    const db = await openDatabase();
    const today = dayBucketOf(Date.now());
    const platformFilter = platforms?.length
      ? new Set(platforms.map((p) => p.toLowerCase()))
      : null;

    let rows: IOverlayEventStatDB[];
    if (scope === "today") {
      // The v26 compound index is [accountId+kind+platform+dayBucket], so
      // there's no compound `[accountId+kind+dayBucket]` to query against.
      // Range over `accountId` and filter the rest in memory — the row
      // count per account/day is small enough that this is fine.
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter((r) => r.kind === kind && r.dayBucket === today)
        .toArray();
    } else if (scope === "session") {
      if (sessionStartedAt === undefined)
        return { count: 0, sumAmountUsd: 0, topAmount: 0, raidViewersSum: 0 };
      const sessionDay = dayBucketOf(sessionStartedAt);
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter(
          (r) =>
            r.kind === kind &&
            r.dayBucket >= sessionDay &&
            r.dayBucket <= today &&
            r.updatedAt >= sessionStartedAt,
        )
        .toArray();
    } else {
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter((r) => r.kind === kind)
        .toArray();
    }

    if (platformFilter) {
      rows = rows.filter((r) => platformFilter.has(r.platform));
    }

    let count = 0;
    let sumAmountUsd = 0;
    let topAmount = -Infinity;
    let topUser: string | undefined;
    let raidViewersSum = 0;
    for (const r of rows) {
      count += r.count;
      sumAmountUsd += r.sumAmountUsd ?? 0;
      if ((r.topAmount ?? -Infinity) > topAmount) {
        topAmount = r.topAmount ?? topAmount;
        topUser = r.topUser ?? topUser;
      }
      raidViewersSum += r.raidViewersSum ?? 0;
    }

    return {
      count,
      sumAmountUsd,
      topAmount: topAmount === -Infinity ? 0 : topAmount,
      topUser,
      raidViewersSum,
    };
  }

  async reset(
    accountId: string,
    kind: StreamEventKind,
    scope: EventStatsScope,
    sessionStartedAt?: number,
  ): Promise<void> {
    const db = await openDatabase();
    const today = dayBucketOf(Date.now());

    let rows: IOverlayEventStatDB[];
    if (scope === "today") {
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter((r) => r.kind === kind && r.dayBucket === today)
        .toArray();
    } else if (scope === "session") {
      if (sessionStartedAt === undefined) return;
      const sessionDay = dayBucketOf(sessionStartedAt);
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter(
          (r) =>
            r.kind === kind &&
            r.dayBucket >= sessionDay &&
            r.dayBucket <= today,
        )
        .toArray();
    } else {
      rows = await db.overlayEventStats
        .where("accountId")
        .equals(accountId)
        .filter((r) => r.kind === kind)
        .toArray();
    }

    await Promise.all(
      rows.map((r) =>
        db.overlayEventStats.delete([
          r.accountId,
          r.kind,
          r.platform,
          r.dayBucket,
        ]),
      ),
    );
  }
}

export const overlayEventStatsRepository = new OverlayEventStatsRepository();
